import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useInquiryCart } from "@/context/InquiryCartContext";
import { brandName } from "@/lib/labels";
import { Minus, Plus, Trash2, ClipboardList, ArrowRight } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";

export default function CartDrawer() {
  const { items, drawerOpen, closeDrawer, count, setQuantity, removeItem, clear } = useInquiryCart();

  return (
    <Sheet open={drawerOpen} onOpenChange={(o) => !o && closeDrawer()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <ClipboardList className="w-5 h-5 text-aqua" />
            Inquiry Cart {count > 0 && <span className="text-greyish font-normal">({count})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center">
            <EmptyState
              icon={ClipboardList}
              title="Your inquiry cart is empty"
              description="Browse our catalogue and add products to build an enquiry."
              actionLabel="Explore Products"
              actionTo="/products"
              testId="cart-empty"
            />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-lenis-prevent>
              {items.map((it) => (
                <div key={it.productId} data-testid={`cart-item-${it.slug}`} className="flex gap-4">
                  <img src={it.image} alt={it.name} loading="lazy" decoding="async" className="w-16 h-16 rounded-md object-cover bg-[#f6f8f9] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.65rem] uppercase tracking-widest font-semibold text-greyish">{brandName(it.brand)}</p>
                    <Link to={`/products/${it.slug}`} onClick={closeDrawer} className="block text-sm font-semibold text-ink truncate hover:text-aqua">{it.name}</Link>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center border border-border rounded-md">
                        <button onClick={() => setQuantity(it.productId, it.quantity - 1)} data-testid={`cart-dec-${it.slug}`} className="w-8 h-8 flex items-center justify-center text-greyish hover:text-ink"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-8 text-center text-sm font-semibold">{it.quantity}</span>
                        <button onClick={() => setQuantity(it.productId, it.quantity + 1)} data-testid={`cart-inc-${it.slug}`} className="w-8 h-8 flex items-center justify-center text-greyish hover:text-ink"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => removeItem(it.productId)} data-testid={`cart-remove-${it.slug}`} aria-label="Remove" className="text-greyish hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-6 space-y-3">
              <Link
                to="/enquiry"
                onClick={closeDrawer}
                data-testid="cart-proceed-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-aqua text-white font-semibold px-6 py-3.5 rounded-md hover:bg-aqua-dark transition-colors"
              >
                Proceed to Enquiry <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex items-center justify-between text-sm">
                <button onClick={closeDrawer} data-testid="cart-continue-btn" className="font-semibold text-ink hover:text-aqua transition-colors">Continue browsing</button>
                <button onClick={clear} data-testid="cart-clear-btn" className="text-greyish hover:text-destructive transition-colors">Clear cart</button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
